import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff, KeyRound, Loader2, Lock, ShieldCheck, AlertCircle } from "lucide-react";
import { BrandMark } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, digits } from "@/lib/api";
import { Field } from "@/components/common";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1728044849280-10a1a75cff83?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const REMEMBER = "pdv_remember";

function ForgotDialog({ open, onOpenChange, mode, defaultAccess }) {
  const [access, setAccess] = useState(defaultAccess || "");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const isPass = mode === "password";
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = isPass
        ? await api.post("/auth/forgot-password", { access_number: access, email })
        : await api.post("/auth/recover-access", { email });
      toast.success(data.message);
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={isPass ? "forgot-password-modal" : "recover-access-modal"}>
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{isPass ? "Recuperar senha" : "Recuperar número de acesso"}</DialogTitle>
            <DialogDescription>
              {isPass ? "Enviaremos um link de redefinição para o e-mail cadastrado." : "Enviaremos os números de acesso vinculados ao seu e-mail."}
            </DialogDescription>
          </DialogHeader>
          {isPass && (
            <Field label="Número de acesso">
              <Input value={access} onChange={(e) => setAccess(digits(e.target.value))} required data-testid="forgot-access-input" />
            </Field>
          )}
          <Field label="E-mail cadastrado">
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email-input" />
          </Field>
          <DialogFooter>
            <Button type="submit" disabled={busy} data-testid="forgot-submit-btn">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Enviar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Login() {
  const saved = JSON.parse(localStorage.getItem(REMEMBER) || "null");
  const location = useLocation();
  const [form, setForm] = useState({
    access_number: location.state?.access_number || saved?.access_number || "",
    login: location.state?.login || saved?.login || "",
    password: "",
    remember: !!saved,
  });
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [unverified, setUnverified] = useState(false);
  const [locked, setLocked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: k === "access_number" ? digits(e.target.value) : e.target.value }));
  const [notice, setNotice] = useState("");
  useEffect(() => { api.get("/platform-info").then((r) => setNotice(r.data.system_notice)).catch(() => {}); }, []);
  useEffect(() => {
    if (window.history.state?.idx) return;
    navigate("/", { replace: true });
    navigate("/login", { state: location.state });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLocked(false);
    setBusy(true);
    try {
      await login(form);
      if (form.remember) localStorage.setItem(REMEMBER, JSON.stringify({ access_number: form.access_number, login: form.login }));
      else localStorage.removeItem(REMEMBER);
      navigate("/app");
    } catch (err) {
      setError(errMsg(err));
      setLocked(err.response?.status === 429);
      setUnverified(err.response?.status === 403 && errMsg(err).startsWith("Confirme seu e-mail"));
    } finally {
      setBusy(false);
    }
  };
  const resend = async () => {
    try {
      const { data } = await api.post("/auth/resend-confirmation", { access_number: form.access_number, login: form.login });
      toast.success(data.message);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO} alt="Ponto de venda" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-slate-950/75" />
        <div className="relative z-10 flex h-full flex-col justify-between p-12 text-white">
          <Link to="/" className="flex w-fit items-center gap-3 transition-opacity hover:opacity-80" title="Voltar ao site" data-testid="login-home-link">
            <BrandMark size="h-11 w-11" testId="login-brand" />
            <span className="font-display text-xl font-bold">FSCsistemas</span>
          </Link>
          <div className="max-w-md space-y-4 fade-up">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-blue-300">Ponto de venda na nuvem</p>
            <h1 className="text-4xl xl:text-5xl font-extrabold leading-tight">Vendas, mesas, delivery e estacionamento em um só lugar.</h1>
            <p className="text-slate-300">Funciona no computador, tablet e celular. Cada empresa em um ambiente isolado e seguro.</p>
          </div>
          <div className="flex gap-6 text-sm text-slate-300">
            <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-400" />Acesso por número exclusivo</span>
            <span className="flex items-center gap-2"><Lock className="h-4 w-4 text-emerald-400" />Bloqueio anti-invasão</span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10 grain">
        <form onSubmit={submit} className="w-full max-w-md space-y-5 fade-up" data-testid="login-form">
          <Link to="/" className="lg:hidden mb-4 flex w-fit items-center gap-3" title="Voltar ao site" data-testid="login-home-link-mobile">
            <BrandMark tone="bg-primary text-primary-foreground" testId="login-brand-mobile" />
            <span className="font-display text-lg font-bold">FSCsistemas</span>
          </Link>
          {notice && <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm flex gap-2" data-testid="login-system-notice"><AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-amber-500" />{notice}</div>}
          <div>
            <p className="eyebrow mb-2">Entrar no sistema</p>
            <h2 className="text-3xl font-bold tracking-tight">Bem-vindo de volta</h2>
            <p className="text-sm text-muted-foreground mt-1">O número de acesso é obrigatório para entrar.</p>
          </div>

          {error && (
            <div className={`flex gap-2 rounded-lg border p-3 text-sm ${locked ? "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400" : "border-red-500/40 bg-red-500/10 text-red-500"}`} data-testid="login-error-message">
              {locked ? <Lock className="h-4 w-4 mt-0.5 shrink-0" /> : <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />}
              <span>{error}
                {unverified && <button type="button" onClick={resend} className="mt-2 block font-semibold underline" data-testid="login-resend-confirmation">Reenviar e-mail de confirmação</button>}
              </span>
            </div>
          )}

          <Field label="Número de acesso">
            <div className="relative">
              <KeyRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={form.access_number} onChange={set("access_number")} required inputMode="numeric" placeholder="Ex.: 12345678"
                className="h-12 pl-9 font-mono text-lg tracking-widest" data-testid="login-access-number-input" />
            </div>
          </Field>
          <Field label="Login ou e-mail">
            <Input value={form.login} onChange={set("login")} required autoComplete="username" className="h-12" data-testid="login-email-input" />
          </Field>
          <Field label="Senha">
            <div className="relative">
              <Input type={show ? "text" : "password"} value={form.password} onChange={set("password")} required autoComplete="current-password"
                className="h-12 pr-11" data-testid="login-password-input" />
              <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" data-testid="login-toggle-password">
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </Field>
          <div className="flex items-center justify-between text-sm">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={form.remember} onCheckedChange={(v) => setForm((f) => ({ ...f, remember: !!v }))} data-testid="login-remember-checkbox" />
              Lembrar acesso
            </label>
            <button type="button" className="text-primary hover:underline" onClick={() => setDialog("password")} data-testid="forgot-password-link">
              Esqueci a senha
            </button>
          </div>
          <Button type="submit" className="w-full h-12 text-base" disabled={busy} data-testid="login-submit-button">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Entrar
          </Button>
          <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground">
            <button type="button" className="hover:text-foreground" onClick={() => setDialog("access")} data-testid="recover-access-link">
              Esqueci meu número de acesso
            </button>
            <span>Ainda não tem conta? <Link to="/cadastro" className="text-primary font-semibold hover:underline" data-testid="go-signup-link">Teste grátis por 15 dias</Link></span>
          </div>
        </form>
      </div>
      <ForgotDialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)} mode={dialog} defaultAccess={form.access_number} key={dialog} />
    </div>
  );
}
